import { CapacitorHttp, type HttpResponse } from "@capacitor/core";
import { isNativePlatform } from "./platform";

export interface NativeFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  responseType?: "json" | "text" | "arraybuffer" | "blob";
}

function getResponseHeader(
  headers: Record<string, string> | undefined,
  name: string,
): string | null {
  if (!headers) {
    return null;
  }

  const target = name.toLowerCase();
  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === target,
  );

  return entry?.[1] ?? null;
}

function decodeBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer;
}

function normalizeNativeResponse(
  response: HttpResponse,
  responseType: NativeFetchOptions["responseType"],
): Response {
  const headers = new Headers();
  if (response.headers) {
    for (const [key, value] of Object.entries(response.headers)) {
      headers.set(key, String(value));
    }
  }

  if (responseType === "arraybuffer") {
    const data =
      typeof response.data === "string"
        ? decodeBase64ToArrayBuffer(response.data)
        : new TextEncoder().encode(JSON.stringify(response.data)).buffer;

    return new Response(data, { status: response.status, headers });
  }

  if (responseType === "blob") {
    const data =
      typeof response.data === "string"
        ? decodeBase64ToArrayBuffer(response.data)
        : new TextEncoder().encode(JSON.stringify(response.data)).buffer;

    const contentType =
      getResponseHeader(response.headers, "content-type") ?? "application/octet-stream";

    return new Response(data, {
      status: response.status,
      headers: new Headers({ ...Object.fromEntries(headers), "content-type": contentType }),
    });
  }

  const body =
    responseType === "text"
      ? String(response.data ?? "")
      : JSON.stringify(response.data ?? null);

  return new Response(body, {
    status: response.status,
    headers: new Headers({
      ...Object.fromEntries(headers),
      "content-type":
        getResponseHeader(response.headers, "content-type") ??
        (responseType === "text" ? "text/plain" : "application/json"),
    }),
  });
}

/**
 * Uses CapacitorHttp on native to avoid WebView CORS/mixed-content issues.
 * Falls back to fetch on web.
 */
export async function nativeFetch(
  url: string,
  options: NativeFetchOptions = {},
): Promise<Response> {
  const { method = "GET", headers, body, responseType = "json" } = options;

  if (isNativePlatform()) {
    const response = await CapacitorHttp.request({
      url,
      method,
      headers,
      data: body,
      responseType: responseType === "arraybuffer" || responseType === "blob" ? "arraybuffer" : "json",
    });

    return normalizeNativeResponse(response, responseType);
  }

  const init: RequestInit = { method, headers };

  if (body !== undefined && method !== "GET") {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  return fetch(url, init);
}
